/**
 * Shared Recipe API helper (window.RecipeApi).
 * Talks to the Next.js content API (shop.metafields.custom.next_api_endpoint)
 * and builds DOM matching this theme's native blogs/recipes look
 * (snippets/article_card_recipe.liquid, snippets/article_main_structure.liquid).
 *
 * Initialised once per page by the first section that loads.
 */
(function () {
  if (window.RecipeApi) return;

  function apiFetch(url) {
    return fetch(url, { method: 'GET', headers: {} }).then(function (r) {
      if (!r.ok) throw new Error('Recipe API request failed: ' + r.status);
      return r.json();
    });
  }

  function toArray(value) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.keys(value).map(function (k) { return value[k]; });
    if (typeof value === 'string') {
      try {
        var parsed = JSON.parse(value);
        return toArray(parsed);
      } catch (e) { return []; }
    }
    return [];
  }

  function stripHtml(html) {
    return (html || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  }

  function toISO8601(dateStr) {
    if (!dateStr) return '';
    var date = new Date(dateStr);
    return isNaN(date.getTime()) ? '' : date.toISOString();
  }

  // Recipe API times come back as plain minute counts (e.g. `30`), but
  // schema.org's Recipe.prepTime/cookTime/totalTime require an ISO 8601
  // duration (e.g. `PT30M`, `PT1H30M`).
  function minutesToISO8601Duration(minutes) {
    var total = Math.round(parseFloat(minutes));
    if (isNaN(total) || total <= 0) return '';
    var hours = Math.floor(total / 60);
    var mins = total % 60;
    return 'PT' + (hours > 0 ? hours + 'H' : '') + (mins > 0 ? mins + 'M' : '');
  }

  // `tags` comes back as a plain "Tag One, Tag Two" string (not JSON), so
  // toArray()'s JSON.parse would throw and silently return []. Mirrors the
  // parseTags() fallback in sections/recipe_api_hero.liquid.
  function parseTags(raw) {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw.trim()) {
      try {
        var parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {}
      return raw.split(',').map(function (t) { return t.trim(); }).filter(Boolean);
    }
    return [];
  }

  window.RecipeApi = {
    _base: '',
    _filtersBase: '',
    _searchBase: '',
    _shop: '',

    init: function (apiBase, shopDomain, filtersBase, searchBase) {
      this._base = apiBase;
      this._shop = shopDomain;
      this._filtersBase = filtersBase || '';
      this._searchBase = searchBase || '';
    },

    fetchDetail: function (identifier) {
      var url = this._base + '/' + encodeURIComponent(identifier) + '?shop=' + encodeURIComponent(this._shop);
      return apiFetch(url);
    },

    fetchListing: function (params) {
      params = params || {};
      var url = this._base + '?shop=' + encodeURIComponent(this._shop)
        + '&page=' + (params.page || 1)
        + '&limit=' + (params.limit || 12);

      if (params.food_types && params.food_types.length) {
        url += '&food_types=' + encodeURIComponent(params.food_types.join(','));
      }
      if (params.difficulties && params.difficulties.length) {
        url += '&difficulties=' + encodeURIComponent(params.difficulties.join(','));
      }
      if (params.meal_types && params.meal_types.length) {
        url += '&meal_types=' + encodeURIComponent(params.meal_types.join(','));
      }
      if (params.products && params.products.length) {
        url += '&products=' + encodeURIComponent(params.products.join(','));
      }

      return apiFetch(url);
    },

    /**
     * GET {next_api_endpoint}recipe-filters?shop=X
     * -> { filters: [{key,label,items:[{value,label}]}] }
     */
    fetchFilters: function () {
      var url = this._filtersBase + '?shop=' + encodeURIComponent(this._shop);
      return apiFetch(url);
    },

    /** Pulls a filter group's items (e.g. 'food_types', 'tags') out of a fetchFilters() response. */
    getFilterItems: function (data, key) {
      var group = ((data && data.filters) || []).find(function (g) { return g.key === key; });
      return group ? group.items : [];
    },

    /**
     * GET {next_api_endpoint}search?shop=X&q=Y&limit=N
     * -> { recipes: [{title, listing_image, identifier}], "how-tos": [...] }
     */
    fetchSearch: function (query, limit) {
      var url = this._searchBase + '?shop=' + encodeURIComponent(this._shop)
        + '&q=' + encodeURIComponent(query || '')
        + '&limit=' + (limit || 10);
      return apiFetch(url);
    },

    /**
     * GET {next_api_endpoint}similar-recipes?shop=X&identifier=Y&limit=N&is_auto=Z
     * -> { recipes: [...], pagination }
     * The merchant-curated `similar_recipes` list on the source recipe (comma-separated ids),
     * or an automatically-selected list when `isAuto` is true.
     */
    fetchSimilar: function (identifier, limit, isAuto) {
      var url = this._base.replace(/\/recipes$/, '/similar-recipes')
        + '?shop=' + encodeURIComponent(this._shop)
        + '&identifier=' + encodeURIComponent(identifier || '')
        + '&limit=' + (limit || 12)
        + '&is_auto=' + (isAuto ? 'true' : 'false');
      return apiFetch(url);
    },

    /**
     * Builds a card matching snippets/article_card_recipe.liquid markup.
     * @param {Object} recipe
     * @param {string} cardsColorScheme
     * @param {string} cardUrlBase - e.g. '/pages/recipes/'
     * @returns {HTMLElement} .grid-item wrapper
     */
    buildCard: function (recipe, cardsColorScheme, cardUrlBase) {
      var identifier = recipe.identifier || '';
      var imageUrl    = recipe.image_listing || recipe.image || '';
      var articleUrl  = (cardUrlBase || '/pages/recipes/') + identifier;
      var title       = recipe.title || '';
      var badge       = (this._foodTypeLabels && this._foodTypeLabels[(recipe.food_type || '').toLowerCase()]) || recipe.food_type || '';

      var prepTime   = recipe.prep_time  != null && recipe.prep_time  !== '' ? recipe.prep_time  + ' min' : '-';
      var cookTime   = recipe.cook_time  != null && recipe.cook_time  !== '' ? recipe.cook_time  + ' min' : '-';
      var serves     = recipe.yield != null && recipe.yield !== '' ? recipe.yield : '-';
      var difficulty = recipe.difficulty != null && recipe.difficulty !== '' ? recipe.difficulty : '-';

      var wrap = document.createElement('div');
      wrap.className = 'grid-item article-item';

      var placeholder = this._placeholderImageListing || '';
      var effectiveImageUrl = imageUrl || placeholder;
      var imgHtml;
      if (effectiveImageUrl) {
        imgHtml = '<div class="img ease-animation-slow lazyload" data-bgset="' + effectiveImageUrl + '">';
        if (imageUrl && placeholder) {
          var safeUrl = placeholder.replace(/'/g, '%27');
          imgHtml += '<img src="' + imageUrl + '" style="display:none" alt="" onerror="var p=this.parentNode;p.removeAttribute(\'data-bgset\');p.classList.remove(\'lazyload\');p.style.backgroundImage=\'url(' + safeUrl + ')\';this.onerror=null;" />';
        }
        imgHtml += '</div>';
      } else {
        imgHtml = '<div class="img ease-animation-slow"></div>';
      }

      wrap.innerHTML =
        '<div class="article-card recipe common-card text-left ' + (cardsColorScheme ? 'scheme color-' + cardsColorScheme : '') + ' same-scheme-false">' +
          '<div class="image-container">' +
            (badge ? '<div class="article-badge">' + badge + '</div>' : '') +
            imgHtml +
          '</div>' +
          '<div class="info">' +
            '<a href="' + articleUrl + '" class="title no-highlight-hover" aria-label="' + window.RecipeApiLabels.recipeViewAria.replace('{{ article_title }}', title) + '">' + title + '</a>' +
            '<div class="recipe-wrapper">' +
              '<div class="info-wrapper">' +
                '<div class="prep">' +
                  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 5.33333V8L10 10M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                  '<div class="data-title"><strong>' + window.RecipeApiLabels.prep + '</strong> <span class="prep-time">' + prepTime + '</span></div>' +
                '</div>' +
                '<div class="cook">' +
                  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.7725 12.4379C9.68974 14.5207 6.31286 14.5207 4.23007 12.4379C3.18867 11.3965 2.66797 10.0316 2.66797 8.66667C2.66797 7.30174 3.18867 5.93682 4.23007 4.89542C4.23007 4.89542 4.668 5.99997 6.00133 6.66664C6.00133 5.3333 6.33466 3.3333 7.99189 2C9.33464 3.33333 10.7287 3.85163 11.7725 4.89542C12.8139 5.93682 13.3346 7.30175 13.3346 8.66667C13.3346 10.0316 12.8139 11.3965 11.7725 12.4379Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.58709 10.7475C7.36814 11.5286 8.63447 11.5286 9.41552 10.7475C9.80603 10.357 10.0013 9.84516 10.0013 9.33333C10.0013 8.82147 9.80605 8.30962 9.41552 7.91909C9.02746 7.53103 8.51961 7.33578 8.01101 7.33333L7.3346 9.33329L6.0013 9.33333C6.00131 9.84516 6.19657 10.357 6.58709 10.7475Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                  '<div class="data-title"><strong>' + window.RecipeApiLabels.cook + '</strong> <span class="cook-time">' + cookTime + '</span></div>' +
                '</div>' +
                '<div aria-hidden="true" class="serves">' +
                  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.6654 4.66667C10.6654 6.13943 9.47146 7.33333 7.9987 7.33333C6.52594 7.33333 5.33203 6.13943 5.33203 4.66667C5.33203 3.19391 6.52594 2 7.9987 2C9.47146 2 10.6654 3.19391 10.6654 4.66667Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/><path d="M7.9987 9.33333C5.42137 9.33333 3.33203 11.4227 3.33203 14H12.6654C12.6654 11.4227 10.576 9.33333 7.9987 9.33333Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                  '<div class="data-title"><strong>' + window.RecipeApiLabels.serves + '</strong> <span class="serves-time">' + serves + '</span></div>' +
                '</div>' +
                '<div class="level">' +
                  '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.668 5.33341V10.6667M8.0013 7.33341V10.6667M5.33464 9.33341V10.6667M4.0013 13.3334H12.0013C12.7377 13.3334 13.3346 12.7365 13.3346 12.0001V4.00008C13.3346 3.2637 12.7377 2.66675 12.0013 2.66675H4.0013C3.26492 2.66675 2.66797 3.2637 2.66797 4.00008V12.0001C2.66797 12.7365 3.26492 13.3334 4.0013 13.3334Z" stroke="#26282A" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                  '<div class="data-title"><strong>' + window.RecipeApiLabels.level + '</strong> <span class="level-time">' + difficulty + '</span></div>' +
                '</div>' +
              '</div>' +
              '<div><a href="' + articleUrl + '" class="article-link">' + window.RecipeApiLabels.recipeView + '</a></div>' +
            '</div>' +
          '</div>' +
        '</div>';

      return wrap;
    },

    /**
     * Renders the ingredients column matching #articleMain .recipe-ingredients markup.
     * Real shape (RecipeForm.tsx IngredientRow[]): flat array of
     * { section, amount, ingredient_prefix, ingredient, ingredient_suffix }, grouped here by `section`.
     */
    renderIngredients: function (container, ingredientsData) {
      var items = toArray(ingredientsData);
      var groups = [];
      var map = {};

      items.forEach(function (item) {
        var key = item.section || '';
        if (!map[key]) { map[key] = { title: key, items: [] }; groups.push(map[key]); }
        map[key].items.push(item);
      });

      var html = '';
      groups.forEach(function (group) {
        if (group.title) html += '<div class="sidebar-title">' + group.title + '</div>';
        group.items.forEach(function (item) {
          var amount = item.amount || '';
          var ingredient = [item.ingredient_prefix, item.ingredient, item.ingredient_suffix]
            .filter(Boolean).join(' ');
          html += '<div class="ingredient-info"><span class="amount">' + amount + '</span><div class="instructions">' + ingredient + '</div></div>';
        });
      });

      container.innerHTML += html;
    },

    /**
     * Renders the steps/directions column matching #articleMain .recipe-content markup.
     * Real shape (RecipeForm.tsx StepRow[]):
     * { identifier, instruction, step_image, image_position, image_alt, step_tip, commands }
     */
    renderDirections: function (container, directionsData, recipe) {
      var steps = toArray(directionsData);
      var html = '';

      if (recipe && recipe.top_content) {
        html += '<div class="top-content">' + recipe.top_content + '</div>';
      }

      steps.forEach(function (step, i) {
        var content = step.instruction || '';
        var tip = step.step_tip || '';

        html += '<div class="step"><span class="number">' + (i + 1) + '.</span><div class="instructions">' + content;

        if (tip) {
          html += '<div class="tip"><span>' + window.RecipeApiLabels.tip + '</span><span>' + tip + '</span></div>';
        }

        if (step.step_image) {
          html += '<img class="lazyload step-media" alt="' + (step.image_alt || '') + '" src="' + step.step_image + '" />';
        }

        html += '</div></div>';
      });

      if (recipe && recipe.bottom_content) {
        html += '<div class="bottom-content">' + recipe.bottom_content + '</div>';
      }

      container.innerHTML = html;
    },

    /**
     * Injects a Recipe JSON-LD block (schema.org/Recipe) for pages/recipes/{identifier},
     * sourced from the recipe API response plus the page's own metaobject SEO description.
     */
    injectStructuredData: function (recipe, pageUrl, shopName, seoDescription) {
      try {
        var tags = parseTags(recipe.tags);
        var description = seoDescription
          || recipe.meta_description
          || (recipe.short_content ? stripHtml(recipe.short_content) : '');

        var prepTime = minutesToISO8601Duration(recipe.prep_time);
        var cookTime = minutesToISO8601Duration(recipe.cook_time);
        var totalTime = minutesToISO8601Duration(
          (parseFloat(recipe.prep_time) || 0) + (parseFloat(recipe.cook_time) || 0)
        );

        var recipeIngredient = toArray(recipe.ingredients_row).map(function (item) {
          return [item.amount, item.ingredient_prefix, item.ingredient, item.ingredient_suffix]
            .filter(Boolean).join(' ').trim();
        }).filter(Boolean);

        var recipeInstructions = toArray(recipe.steps).map(function (step) {
          var howToStep = { '@type': 'HowToStep', 'text': stripHtml(step.instruction || '') };
          if (step.step_image) howToStep.image = step.step_image;
          return howToStep;
        });

        var ld = {
          '@context': 'https://schema.org/',
          '@type': 'Recipe',
          'name': recipe.title || '',
          'image': recipe.image || recipe.image_listing || '',
          'author': {
            '@type': 'Organization',
            'name': shopName
          },
          'datePublished': toISO8601(recipe.created_at),
          'dateModified': toISO8601(recipe.updated_at),
          'description': description,
          'url': pageUrl,
          'keywords': tags.join(',')
        };

        if (prepTime) ld.prepTime = prepTime;
        if (cookTime) ld.cookTime = cookTime;
        if (totalTime) ld.totalTime = totalTime;
        if (recipe.yield != null && recipe.yield !== '') ld.recipeYield = recipe.yield;
        if (recipe.meal_type) ld.recipeCategory = recipe.meal_type;
        if (recipe.cuisine) ld.recipeCuisine = recipe.cuisine;
        if (recipeIngredient.length) ld.recipeIngredient = recipeIngredient;
        if (recipeInstructions.length) ld.recipeInstructions = recipeInstructions;
        if (recipe.video_url) {
          ld.video = {
            '@type': 'VideoObject',
            'name': recipe.title || '',
            'description': description,
            'thumbnailUrl': recipe.image || recipe.image_listing || '',
            'contentUrl': recipe.video_url,
            'uploadDate': ld.datePublished
          };
        }

        var script = document.createElement('script');
        script.type = 'application/ld+json';
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      } catch (e) {
        console.error('[RecipeApi] structured data injection failed:', e);
      }
    },

    /**
     * Sets the Meta Title (<title>) and Meta Description (<meta name="description">),
     * and updates/creates the og: and twitter: tags snippets/meta_tags.liquid already
     * renders (with generic page_title/page_description values, since there's
     * no native `page_image`/article object here), matching what the blog
     * article template outputs. Note: unlike Googlebot, social-preview
     * crawlers (Facebook/Twitter/Slack/etc.) don't execute JS, so these
     * updated values won't be seen in link-preview cards for this URL — only
     * by on-page/JS-rendering consumers.
     */
    injectMetaTags: function (recipe, pageUrl, seoDescription, seoTitle, siteName) {
      try {
        function setMeta(attr, key, content) {
          if (content == null || content === '') return;
          var el = document.querySelector('head meta[' + attr + '="' + key + '"]');
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute(attr, key);
            document.head.appendChild(el);
          }
          el.setAttribute('content', String(content));
        }

        var title = recipe.title || '';
        // Meta Title priority: metaobject SEO title > API meta_title > "<title> – <site name>"
        var metaTitle = seoTitle || recipe.meta_title
          || (title && siteName ? title + ' \u2013 ' + siteName : title);
        var description = seoDescription
          || recipe.meta_description
          || stripHtml(recipe.content || '').slice(0, 160);
        var imageUrl = recipe.image || recipe.image_listing || '';

        if (metaTitle) document.title = metaTitle;
        setMeta('name', 'description', description);

        setMeta('property', 'og:url', pageUrl);
        setMeta('property', 'og:type', 'article');
        setMeta('property', 'og:title', metaTitle);
        setMeta('property', 'og:description', description);
        setMeta('name', 'twitter:title', metaTitle);
        setMeta('name', 'twitter:description', description);

        if (imageUrl) {
          setMeta('property', 'og:image', imageUrl);
          setMeta('property', 'og:image:secure_url', imageUrl.replace(/^http:/, 'https:'));
          setMeta('name', 'twitter:image', imageUrl);

          // Dimensions aren't provided by the recipe API, so read them off the
          // loaded image itself rather than guessing/omitting.
          var probe = new Image();
          probe.onload = function () {
            setMeta('property', 'og:image:width', probe.naturalWidth);
            setMeta('property', 'og:image:height', probe.naturalHeight);
          };
          probe.src = imageUrl;
        }
      } catch (e) {
        console.error('[RecipeApi] meta tag injection failed:', e);
      }
    },

    /**
     * Injects an ItemList JSON-LD block for pages/recipes (the listing page),
     * rebuilt after every fetchListing() resolution (initial load + Load More)
     * so it always reflects what's currently in the grid.
     */
    injectListingStructuredData: function (recipes, cardUrlBase) {
      try {
        var scriptId = 'recipe-listing-structured-data';
        var script = document.getElementById(scriptId);
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }

        var ld = {
          '@context': 'https://schema.org/',
          '@type': 'ItemList',
          'itemListElement': recipes.map(function (recipe, index) {
            return {
              '@type': 'ListItem',
              'position': index + 1,
              'name': recipe.title || '',
              'url': window.location.origin + (cardUrlBase || '/pages/recipes/') + (recipe.identifier || ''),
              'image': recipe.image_listing || recipe.image || ''
            };
          })
        };

        script.textContent = JSON.stringify(ld);
      } catch (e) {
        console.error('[RecipeApi] listing structured data injection failed:', e);
      }
    },

    /**
     * Overrides document.title and the description/og/twitter meta tags on the
     * recipe listing page, which otherwise fall through to the generic
     * page_title/shop.description defaults in snippets/meta_tags.liquid since
     * this page has no native Shopify content for SEO copy to be set on.
     * Values come from the section's own settings; a blank setting leaves the
     * existing tag untouched.
     */
    injectListingMetaTags: function (seoTitle, seoDescription) {
      try {
        function setMeta(attr, key, content) {
          if (content == null || content === '') return;
          var el = document.querySelector('head meta[' + attr + '="' + key + '"]');
          if (!el) {
            el = document.createElement('meta');
            el.setAttribute(attr, key);
            document.head.appendChild(el);
          }
          el.setAttribute('content', String(content));
        }

        if (seoTitle) document.title = seoTitle;
        setMeta('name', 'description', seoDescription);
        setMeta('property', 'og:title', seoTitle);
        setMeta('property', 'og:description', seoDescription);
        setMeta('name', 'twitter:title', seoTitle);
        setMeta('name', 'twitter:description', seoDescription);
      } catch (e) {
        console.error('[RecipeApi] listing meta tag injection failed:', e);
      }
    },

    /**
     * Strips query params (e.g. ?category=, ?product= filter state) off the
     * canonical link so filtered listing URLs aren't indexed as separate
     * duplicate-content pages; they should all canonicalize back to the
     * plain listing URL.
     */
    enforceCanonical: function () {
      try {
        var link = document.querySelector('link[rel="canonical"]');
        if (!link) return;
        link.setAttribute('href', window.location.origin + window.location.pathname);
      } catch (e) {
        console.error('[RecipeApi] canonical enforcement failed:', e);
      }
    }
  };

  window.RecipeApiLabels = window.RecipeApiLabels || {
    prep: 'PREP:', cook: 'COOK:', serves: 'SERVES:', level: 'LEVEL:', tip: 'HOT TIP',
    recipeView: 'View Recipe', recipeViewAria: 'View Recipe: {{ article_title }}'
  };
})();
